/**
 * Service layer — a project's Reference Documents (docs/REFERENCE_DOCUMENTS.md).
 * Talks to /api/reference-documents only. The files we receive, kept once per project; a file can
 * answer a kit item, and the Kit row links to it. Uploading never ticks the item.
 */

async function authToken() {
  if (typeof window === 'undefined') return null
  for (let i = 0; i < 20; i++) {
    if (typeof window.__clerkGetToken === 'function') {
      try { const t = await window.__clerkGetToken(); if (t) return t } catch { /* not ready */ }
    }
    await new Promise(r => setTimeout(r, 150))
  }
  return null
}
async function post(body) {
  const token = await authToken()
  const res = await fetch('/api/reference-documents', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: 'Bearer ' + token } : {}) },
    body: JSON.stringify(body),
  })
  const json = await res.json().catch(() => ({}))
  if (!res.ok) throw new Error(json.error || ('Request failed: ' + res.status))
  return json
}

/** { documents, facts } — facts are the kit items that arrive as a file, for the "which item" picker. */
export function listReferenceDocuments(projectId) { return post({ action: 'list', projectId }) }

/** Uploads one file to the project. factId tags the kit item it answers (optional). → { document } */
export async function uploadReferenceDocument({ projectId, file, factId = null, memberId = null, memberName = null }) {
  const { signedUrl, storagePath } = await post({ action: 'getUploadUrl', projectId, filename: file.name })
  const put = await fetch(signedUrl, { method: 'PUT', headers: { 'Content-Type': file.type || 'application/octet-stream' }, body: file })
  if (!put.ok) throw new Error('Upload failed (' + put.status + ')')
  return post({
    action: 'confirmUpload', projectId, storagePath, name: file.name,
    fileType: (file.name.split('.').pop() || '').toLowerCase() || null, sizeBytes: file.size,
    factId, memberId, memberName,
  })
}

/** Opens a document in a new tab. The tab is opened first so the browser doesn't block it. */
export async function openReferenceDocument(id) {
  const w = window.open('about:blank', '_blank')
  try {
    const { url } = await post({ action: 'openUrl', id })
    if (w) w.location.href = url
    else window.open(url, '_blank')
  } catch (e) {
    if (w) w.close()
    throw e
  }
}

export function updateReferenceDocument(id, patch) { return post({ action: 'update', id, ...patch }) }
export function deleteReferenceDocument(id) { return post({ action: 'delete', id }) }

/** Asks the browser for files. Resolves to an array (empty when cancelled). */
export function pickFiles({ multiple = true } = {}) {
  return new Promise(resolve => {
    const input = document.createElement('input')
    input.type = 'file'
    input.multiple = multiple
    input.style.display = 'none'
    let done = false
    input.addEventListener('change', () => { done = true; resolve(Array.from(input.files || [])); input.remove() })
    input.addEventListener('cancel', () => { done = true; resolve([]); input.remove() })
    // Older browsers fire no cancel event; the window regains focus instead.
    window.addEventListener('focus', () => setTimeout(() => { if (!done) { resolve([]); input.remove() } }, 800), { once: true })
    document.body.appendChild(input)
    input.click()
  })
}

/** Tells every screen showing this project's documents (and the kits that link to them) to reload. */
export function announceReferenceDocumentsChanged(projectId) {
  window.dispatchEvent(new CustomEvent('reference-documents-changed', { detail: { projectId } }))
}
